import { DateTime } from 'luxon';
import moment from 'moment'
import { UserSubscription } from 'shared/models/user-settings.model';

interface NameInfo {
  firstName: string;
  lastName: string;
}

export class Utils {

  /**
   * Calculates the number of remaining days for a subscription.
   * - If the status is 'trialing', it checks against 'trialEndsAt'.
   * - If the status is 'active', it checks against 'subscriptionCurrentPeriodEnd'.
   * - For any other status, or if dates are invalid/missing, it returns 0.
   * @param subscription The user's subscription object.
   * @returns The number of whole days remaining.
   */
  static checkSubscriptionDays(subscription: UserSubscription): number {
    if (!subscription) {
      return 0;
    }

    let dateToCheck: Date | undefined;

    if (subscription.subscriptionStatus === 'trialing') {
      dateToCheck = subscription.trialEndsAt;
    } else if (subscription.subscriptionStatus === 'active') {
      dateToCheck = subscription.subscriptionCurrentPeriodEnd;
    } else {
      // For statuses like 'past_due', 'canceled', 'unpaid', there are no active days.
      return 0;
    }

    if (!dateToCheck) {
      return 0;
    }

    const endDate = DateTime.fromISO(dateToCheck as any).endOf('day');
    const now = DateTime.now();

    if (!endDate.isValid) {
      return 0;
    }

    // Calculate the difference in days and return the floor value.
    const diff = endDate.diff(now, 'days').toObject();
    return Math.floor(diff.days ?? 0);
  }

  static getMonthName(monthIndex) {
    const months = [
        "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
        "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
    ];
    return months[monthIndex];
  }
  
  static formatBankAccount(accountNumber) {
    // Match the first two characters, then group the remaining digits in sets of four
    accountNumber = accountNumber.replace(/^([\w]{2})([\w]+)/, '$1 $2 ').replace(/(\w{4})(?=\w)/g, '$1 ');
  
    return accountNumber;
  }
  
  static capitalizeFirstLetter(word: string): string {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }
  
  static extractNameInfo(fullName: string): NameInfo {
    const nameParts = fullName.split(' ');
  
    let firstName = '';
    let lastName = '';
  
    if (nameParts.length >= 1) {
      firstName = this.capitalizeFirstLetter(nameParts[0]);
  
      if (nameParts.length > 1) {
        // Capitalize the first letter of each word in the last name
        lastName = nameParts.slice(1).map(this.capitalizeFirstLetter).join(' ');
      }
    }
  
    return { firstName, lastName };
  }
 

  static getRest(amount:any) {
    if (typeof amount == 'string') {
      amount = amount.replaceAll(',', '.')
      amount = amount.replaceAll(' ', '')
    }
    // Sprawdź, czy wartość jest liczbą lub stringiem
    if (typeof amount !== 'number') {
      // Spróbuj zamienić na liczbę, zachowując resztę
      amount = parseFloat(amount);

      // Sprawdź, czy format jest poprawny
      if (isNaN(amount)) {
        return '00';
      }
    }

    // Zaokrąglij do dwóch miejsc po przecinku
    const roundedAmount = Math.round(amount * 100) / 100;

    // Wyodrębnij część dziesiętną
    const decimalPart = roundedAmount % 1;

    // Zaokrąglij do dwóch miejsc po przecinku
    const roundedDecimalPart = Math.round(decimalPart * 100);

    // Dodaj zero z przodu, jeżeli reszta jest mniejsza niż 10
    const formattedRest = roundedDecimalPart < 10 ? '0' + roundedDecimalPart : roundedDecimalPart.toString();

    return formattedRest;
  }

  static getInvoiceMonth(month: number) {
    month = month + 1;
    if (month < 10) return "0" + month;
    return month;
  }

  static numberInDocument(day: number) {
    day = day;
    if (day < 10) return "0" + day;
    return day;
  }

  static toDecimal(value: string | number | Number): string {
    // Handling the case where value is '-0.00' and converting it to 0
    if (value === '-0.00') value = 0;

    // Checking if value is undefined, if yes, assigning it 0
    if (typeof value === 'undefined') value = 0;

    // Converting non-string values to fixed-point notation with 2 decimal places
    if (typeof value !== 'string') {
        value = Number(value).toFixed(2);
    } else {
        // Replacing commas with periods if present
        value = value.replace(',', '.');
    }

    // Splitting the string into integer and decimal parts
    const str: string = value.toString();
    let [integerPart, decimalPart] = str.split('.');

    // Formatting integer part with thousands separator
    const parts: string[] = [];
    for (let i = integerPart.length; i > 0; i -= 3) {
        parts.unshift(integerPart.slice(Math.max(0, i - 3), i));
    }

    // Concatenating integer and decimal parts with appropriate formatting
    let result: string = decimalPart ? parts.join(' ') + ',' + decimalPart : parts.join(' ');

    // Handling the case where result is '-0,00' and converting it to '0,00'
    if (result === '-0,00') result = '0,00';

    return result;
}
  static toStringDate(date: Date | string, showHours = false) {
    if (typeof date == 'string') { return date }
    if (!showHours) return `${this.numberInDocument(date.getDate())}-${this.getInvoiceMonth(date.getMonth())}-${date.getFullYear()}`
    else return `${this.numberInDocument(date.getDate())}-${this.getInvoiceMonth(date.getMonth())}-${date.getFullYear()} ${this.numberInDocument(date.getHours())}:${this.numberInDocument(date.getMinutes())}`
  }
  static getObjectsInRange(arr: [], min, max): any {
    if(max < 0) max = 0
    // Sprawdzamy, czy indeksy min i max są poprawne i nie wykraczają poza granice tablicy
    if (min < 0 || min > max) {
      return []; // Zwracamy null, jeśli indeksy są niepoprawne
    }

    if (max > arr.length - 1) { max = arr.length - 1 }

    const result = [];
    // Iterujemy po tablicy i dodajemy obiekty, których indeks znajduje się w zakresie min i max
    for (let i = min; i <= max; i++) {
      result.push(arr[i]);
    }

    return result;
  }
  static ParseToObject(string): any {
    if (this.isJSON(string)) { return JSON.parse(string) }
    return {};
  }
  static isJSON(string) {
    try {
      const t = JSON.parse(string);
      return true;
    }
    catch (err) {
      return false;
    }
  }

  static isNullOrEmpty(value: any): boolean {
    // Check if value is undefined, null, or an empty string
    if (typeof value === 'undefined' || value === null || value === '') {
      return true;
    }

    // Check if the value is a string and contains only whitespace
    if (typeof value === 'string' && value.trim() === '') {
      return true;
    }

    return false;
  }
  
  static isDefined(obj, path) {
    if (obj == null) { return false; }
    const parts = path.split('.');
    let result = obj;
    for (let i = 0; i < parts.length; i++) {
      result = result[parts[i]];
      if (result == null) {
        return false;
      }
      else if (typeof result == 'undefined') {
        return false;
      }
    }
    return true;
  }

  static clearUTC(date: any) {
    var clearUTCDate = moment(date).utcOffset(0, true).format()
    return clearUTCDate
  }

  static parseJSONToClass<T>(json: string, clazz: { new(): T }): T {
    const obj = JSON.parse(json);
    const instance: any = new clazz();

    for (const key in obj) {
      if (instance.hasOwnProperty(key) && !key.startsWith('_')) {
        if (Array.isArray(obj[key])) {
          const array: any = [];
          for (const item of obj[key]) {
            if (typeof item === "object" && item !== null) {
              const parsedItem: any = this.parseJSONToClass(JSON.stringify(item), item.constructor);
              array.push(parsedItem);
            } else {
              array.push(item);
            }
          }
          instance[key] = array;
        } else if (typeof obj[key] === "object" && obj[key] !== null) {
          instance[key] = this.parseJSONToClass(JSON.stringify(obj[key]), instance[key].constructor);
        } else {
          instance[key] = obj[key];
        }
      }
    }

    return instance;
  }

  static areObjectValuesEqual(obj1: any, obj2: any): boolean {
    if (typeof obj1 == 'undefined' || obj1 == null) return false;
    if (typeof obj2 == 'undefined' || obj2 == null) return false;
    // Sprawdź czy oba argumenty są obiektami
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
      return false;
    }

    // Pobierz klucze z obu obiektów
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    // Sprawdź czy liczba kluczy jest taka sama
    if (keys1.length !== keys2.length) {
      return false;
    }

    // Iteruj się po kluczach i sprawdź wartości
    for (const key of keys1) {
      // Sprawdź czy oba obiekty mają ten sam klucz
      if (!obj2.hasOwnProperty(key)) {
        return false;
      }

      // Sprawdź czy wartości są takie same
      if (obj1[key] !== obj2[key]) {
        return false;
      }
    }

    // Jeśli przejście przez pętlę nie zwróciło false, to wartości są takie same
    return true;
  }

  static shortenText(text: string, maxLength: number): string {

    const textWithoutNewlines = text.replace(/\n/g, " ");

    if (textWithoutNewlines.length <= maxLength) {
      return textWithoutNewlines;
    } else {
      const shortenedText = textWithoutNewlines.substr(0, maxLength).trim();
      const lastSpaceIndex = shortenedText.lastIndexOf(" ");

      if (lastSpaceIndex !== -1) {
        return shortenedText.substr(0, lastSpaceIndex) + "...";
      } else {
        return shortenedText + "...";
      }
    }
  }
  
}

